import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Login from './components/Login';
import PublisherLogin from './components/PublisherLogin';
import PublisherLayout from './components/PublisherLayout';
import PublisherDashboard from './pages/PublisherDashboard';
import PublisherOffers from './pages/PublisherOffers';
import PublisherDailyReport from './pages/PublisherDailyReport';
import PublisherEventReport from './pages/PublisherEventReport';
import PublisherSettings from './pages/PublisherSettings';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Advertisers from './pages/Advertisers';
import AddAdvertiser from './pages/AddAdvertiser';
import EditAdvertiser from './pages/EditAdvertiser';
import ViewAdvertiser from './pages/ViewAdvertiser';
import Affiliates from './pages/Affiliates';
import AddAffiliate from './pages/AddAffiliate';
import EditAffiliate from './pages/EditAffiliate';
import ViewAffiliate from './pages/ViewAffiliate';
import Offers from './pages/Offers';
import AddOffer from './pages/AddOffer';
import EditOffer from './pages/EditOffer';
import ViewOffer from './pages/ViewOffer';
import Billing from './pages/Billing';
import DailyReport from './pages/DailyReport';
import ConversionReport from './pages/ConversionReport';
import GoalReport from './pages/GoalReport';
import Settings from './pages/Settings';
import CustomPayouts from './pages/CustomPayouts';
import TopOffers from './pages/TopOffers';
import ActivityLog from './pages/ActivityLog';
import SystemOffers from './pages/SystemOffers';
import DLabIntegration from './pages/DLabIntegration';
import AdvertiserIntegrations from './pages/AdvertiserIntegrations';
import PublisherAPI from './pages/PublisherAPI';
import AdminPublisherView from './pages/AdminPublisherView';
import PlaceholderPage from './pages/PlaceholderPage';
import './App.css';

/**
 * AdBizz Media Domain Structure:
 * - admin.adbizzmedia.com - Admin dashboard (this app)
 * - publisher.adbizzmedia.com - Publisher dashboard
 * - track.adbizzmedia.com - Tracking server for affiliate links
 *
 * This app handles both admin and publisher authentication/dashboards
 * depending on the login type selected.
 */
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState<'admin' | 'publisher' | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loginType, setLoginType] = useState<'admin' | 'publisher'>('admin');

  // Check for existing session on app load
  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedUserType = localStorage.getItem('userType') as 'admin' | 'publisher' | null;
    const savedUsername = localStorage.getItem('username');

    if (savedToken && savedUserType) {
      setToken(savedToken);
      setUserType(savedUserType);
      setUsername(savedUsername);
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const handleAdminLogin = (authToken: string) => {
    setToken(authToken);
    setUserType('admin');
    setUsername('admin');
    setIsAuthenticated(true);
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('userType', 'admin');
    localStorage.setItem('username', 'admin');
  };

  const handlePublisherLogin = (publisherUsername: string, userRole: string) => {
    const authToken = 'pub_' + Math.random().toString(36).substring(2, 15);
    setToken(authToken);
    setUserType('publisher');
    setUsername(publisherUsername);
    setIsAuthenticated(true);
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('userType', 'publisher');
    localStorage.setItem('username', publisherUsername);
  };

  const handleLogout = () => {
    setToken(null);
    setUserType(null);
    setUsername(null);
    setIsAuthenticated(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('username');
    localStorage.removeItem('publisherToken');
    localStorage.removeItem('publisherId');
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
        color: '#ecf0f1'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    if (loginType === 'admin') {
      return (
        <Login
          onLogin={handleAdminLogin}
          onSwitchToPublisher={() => setLoginType('publisher')}
        />
      );
    } else {
      return (
        <PublisherLogin
          onLogin={handlePublisherLogin}
          onSwitchToAdmin={() => setLoginType('admin')}
        />
      );
    }
  }

  // Publisher Dashboard with Routing
  if (userType === 'publisher') {
    return (
      <Router>
        <Routes>
          <Route path="/publisher" element={<PublisherLayout onLogout={handleLogout} publisherName={username || ''} />}>
            <Route index element={<PublisherDashboard publisherUsername={username || ''} />} />
            <Route path="dashboard" element={<PublisherDashboard publisherUsername={username || ''} />} />
            <Route path="offers" element={<PublisherOffers publisherUsername={username || ''} />} />
            <Route path="daily-report" element={<PublisherDailyReport />} />
            <Route path="event-report" element={<PublisherEventReport />} />
            <Route path="install-report" element={<PlaceholderPage title="Install Report" />} />
            <Route path="top-offers" element={<PlaceholderPage title="Top Offers" />} />
            <Route path="settings" element={<PublisherSettings publisherUsername={username || ''} />} />
          </Route>
          <Route path="*" element={<PublisherLayout onLogout={handleLogout} publisherName={username || ''} />}>
            <Route index element={<PublisherDashboard publisherUsername={username || ''} />} />
          </Route>
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Layout onLogout={handleLogout} username={username || ''} />}>
          <Route index element={<Dashboard />} />
          <Route path="user-management" element={<PlaceholderPage title="User Management" />} />
          <Route path="managers" element={<PlaceholderPage title="Managers" />} />
          <Route path="advertisers" element={<Advertisers />} />
          <Route path="advertisers/add" element={<AddAdvertiser />} />
          <Route path="advertisers/edit/:id" element={<EditAdvertiser />} />
          <Route path="advertisers/view/:id" element={<ViewAdvertiser />} />
          <Route path="affiliates" element={<Affiliates />} />
          <Route path="affiliates/add" element={<AddAffiliate />} />
          <Route path="affiliates/edit/:id" element={<EditAffiliate />} />
          <Route path="affiliates/view/:id" element={<ViewAffiliate />} />
          <Route path="offers" element={<Offers />} />
          <Route path="offers/add" element={<AddOffer />} />
          <Route path="offers/edit/:id" element={<EditOffer />} />
          <Route path="offers/view/:id" element={<ViewOffer />} />
          <Route path="billing" element={<Billing />} />
          <Route path="managers-report" element={<PlaceholderPage title="Managers Report" />} />
          <Route path="daily-report" element={<DailyReport />} />
          <Route path="conversion-report" element={<ConversionReport />} />
          <Route path="smartlink-report" element={<PlaceholderPage title="Smartlink Report" />} />
          <Route path="chart-report" element={<PlaceholderPage title="Chart Report" />} />
          <Route path="goal-report" element={<GoalReport />} />
          <Route path="install-report" element={<PlaceholderPage title="Install Report" />} />
          <Route path="settings" element={<Settings />} />
          <Route path="custom-payouts" element={<CustomPayouts />} />
          <Route path="top-offers" element={<TopOffers />} />
          <Route path="activity-log" element={<ActivityLog />} />
          <Route path="system-offers" element={<SystemOffers />} />
          <Route path="dlab-integration" element={<DLabIntegration />} />
          <Route path="advertiser-integrations" element={<AdvertiserIntegrations />} />
          <Route path="publisher-api" element={<PublisherAPI />} />
          <Route path="publisher-view/:id/*" element={<AdminPublisherView />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
